import './styles.css'

import { sdk } from '@farcaster/miniapp-sdk'

const appRoot = document.querySelector<HTMLDivElement>('#app')

if (!appRoot) {
  throw new Error('Failed to initialise app container')
}

appRoot.innerHTML = `
  <main class="layout">
    <header class="layout__header">
      <h1 class="layout__title">Create Poll</h1>
    </header>
    <section class="layout__content">
      <div class="debug">
        <h2 class="debug__title">Debug</h2>
        <div class="debug__group">
          <h3 class="debug__subtitle">Mini App SDK</h3>
          <div class="btns">
            <button id="btn-context" class="btn">context</button>
            <button id="btn-getCapabilities" class="btn">getCapabilities</button>
            <button id="btn-getChains" class="btn">getChains</button>
            <button id="btn-isInMiniApp" class="btn">isInMiniApp</button>
          </div>
        </div>
        <div class="debug__group">
          <h3 class="debug__subtitle">Actions</h3>
          <div class="btns">
            <button id="btn-addMiniApp" class="btn">addMiniApp</button>
            <button id="btn-close" class="btn">close</button>
            <button id="btn-composeCast" class="btn">composeCast</button>
            <button id="btn-logHello" class="btn">Log Hello</button>
            <button id="btn-signin" class="btn">signin</button>
            <button id="btn-openUrl" class="btn">openUrl</button>
            <button id="btn-viewProfile" class="btn">viewProfile</button>
            <button id="btn-viewCast" class="btn">viewCast</button>
            <button id="btn-swapToken" class="btn">swapToken</button>
            <button id="btn-sendToken" class="btn">sendToken</button>
            <button id="btn-viewToken" class="btn">viewToken</button>
          </div>
        </div>
        <div class="debug__group">
          <h3 class="debug__subtitle">Wallet</h3>
          <button id="btn-getEthProvider" class="btn">getEthereumProvider</button>
          <div class="btns btns--wallet">
            <button id="btn-ethChainId" class="btn">eth_chainId</button>
            <button id="btn-ethBlockNumber" class="btn">eth_blockNumber</button>
          </div>
        </div>
        <div class="debug__group debug__group--output">
          <h3 class="debug__subtitle">Output</h3>
          <div id="debug-output" class="debug__output" role="log" aria-live="polite" aria-label="Debug output"></div>
        </div>
      </div>
    </section>
  </main>
  <noscript class="layout__noscript">JavaScript is required to use the poll mini app.</noscript>
`

const outputContainer = document.querySelector<HTMLDivElement>('#debug-output')
const OUTPUT_MAX_ENTRIES = 20

function formatOutputPayload(value: unknown): string {
  if (value instanceof Error) {
    return value.stack ?? `${value.name}: ${value.message}`
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined) {
    return String(value)
  }

  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function writeOutput(label: string, payload?: unknown) {
  if (!outputContainer) return

  const entry = document.createElement('div')
  entry.className = 'debug__outputEntry'

  const timestamp = new Date().toLocaleTimeString()
  const formattedPayload = payload === undefined ? '' : formatOutputPayload(payload)
  entry.textContent = formattedPayload ? `[${timestamp}] ${label} ${formattedPayload}` : `[${timestamp}] ${label}`

  outputContainer.prepend(entry)

  while (outputContainer.childElementCount > OUTPUT_MAX_ENTRIES) {
    const last = outputContainer.lastElementChild
    if (!last) break
    outputContainer.removeChild(last)
  }
}

function logDebug(label: string, payload?: unknown) {
  if (payload === undefined) {
    console.log(label)
  } else {
    console.log(label, payload)
  }

  writeOutput(label, payload)
}

const prepareMiniApp = async () => {
  try {
    await sdk.actions.ready()
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('sdk.actions.ready() failed; running outside Farcaster host?', error)
    }
  }
}

prepareMiniApp()

type ActionHandler = () => Promise<void>

function onClick(id: string, handler: ActionHandler) {
  const el = document.getElementById(id)
  if (!el) return

  el.addEventListener('click', () => {
    handler().catch((err) => {
      console.error(`[debug:${id}] error`, err)
      writeOutput(`[debug:${id}] error`, err)
    })
  })
}

onClick('btn-context', async () => {
  const context = await sdk.context
  logDebug('[debug] context:', context)
})

onClick('btn-getCapabilities', async () => {
  const capabilities = await sdk.getCapabilities()
  logDebug('[debug] getCapabilities result:', capabilities)
})

onClick('btn-getChains', async () => {
  const chains = await sdk.getChains()
  logDebug('[debug] getChains result:', chains)
})

onClick('btn-isInMiniApp', async () => {
  const isInMiniApp = await sdk.isInMiniApp()
  logDebug('[debug] isInMiniApp result:', isInMiniApp)
})

onClick('btn-addMiniApp', async () => {
  const result = await sdk.actions.addMiniApp()
  logDebug('[debug] addMiniApp result:', result)
})

onClick('btn-close', async () => {
  await sdk.actions.close()
  logDebug('[debug] close invoked')
})

onClick('btn-composeCast', async () => {
  const result = await sdk.actions.composeCast({ text: 'Hello from Poll App', embeds: ['https://pollapp-miniapp.pages.dev/?hello=true'] })
  logDebug('[debug] composeCast result:', result)
})

onClick('btn-logHello', async () => {
  logDebug('[debug] Hello from Miniapp')
})

onClick('btn-signin', async () => {
  const result = await sdk.actions.signIn({ nonce: '1234567890' })
  logDebug('[debug] signIn result:', result)
})

onClick('btn-openUrl', async () => {
  const url = 'https://farcaster.xyz'
  await sdk.actions.openUrl(url)
  logDebug('[debug] openUrl invoked with', url)
})

onClick('btn-viewProfile', async () => {
  const fid = 1
  await sdk.actions.viewProfile({ fid })
  logDebug('[debug] viewProfile invoked for fid', fid)
})

onClick('btn-viewCast', async () => {
  const hash = '0x0123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef'
  await sdk.actions.viewCast({ hash })
  logDebug('[debug] viewCast invoked for hash', hash)
})

onClick('btn-swapToken', async () => {
  const result = await sdk.actions.swapToken({
    sellToken: 'eip155:8453/erc20:0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    buyToken: 'eip155:10/native',
    sellAmount: '100000'
  })
  logDebug('[debug] swapToken result:', result)
})

onClick('btn-sendToken', async () => {
  const result = await sdk.actions.sendToken({
    token: 'eip155:8453/erc20:0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    amount: '1000000',
    recipientFid: 1
  })
  logDebug('[debug] sendToken result:', result)
})

onClick('btn-viewToken', async () => {
  await sdk.actions.viewToken({
    token: 'eip155:8453/erc20:0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  })
  logDebug('[debug] viewToken invoked')
})

onClick('btn-getEthProvider', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  logDebug('[debug] getEthereumProvider:', provider)
})

onClick('btn-ethChainId', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_chainId unavailable: provider missing request method')
    return
  }

  const chainId = await provider.request({ method: 'eth_chainId' })
  logDebug('[debug] eth_chainId result:', chainId)
})

onClick('btn-ethBlockNumber', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_blockNumber unavailable: provider missing request method')
    return
  }

  const blockNumber = await provider.request({ method: 'eth_blockNumber' })
  logDebug('[debug] eth_blockNumber result:', blockNumber)
})
